<template>
  <transition name="slide-in">
    <div
      v-if="shouldShow"
      class="floating-promo-button"
      :class="{ 'is-expanded': isExpanded }"
      role="button"
      tabindex="0"
      :aria-label="text"
      :aria-expanded="isExpanded"
      @click="handleClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @keydown.enter="handleClick"
      @keydown.space.prevent="handleClick"
    >
      <div class="floating-promo-button__content">
        <img
          :src="iconSrc"
          alt="Promo icon"
          class="floating-promo-button__icon-img"
        >
        <span class="floating-promo-button__text">{{ $t(text) }}</span>
      </div>
      <div class="floating-promo-button__sparkle" />
      <!-- Yıldızlar - butonun dışına taşacak -->
      <div class="floating-promo-button__stars">
        <span class="star star--1">✦</span>
        <span class="star star--2">✦</span>
        <span class="star star--3">✦</span>
        <span class="star star--4">✦</span>
        <span class="star star--5">✦</span>
        <span class="star star--6">✦</span>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'FloatingPromoButton',

  props: {
    text: {
      type: String,
      default: 'Claim Free Diamond',
    },
    targetRoute: {
      type: [String, Object],
      default: () => ({ name: 'tasks' }),
    },
    iconSrc: {
      type: String,
      default: '/assets/images/task/task-gift-box.png',
    },
    excludedRoutes: {
      type: Array,
      default: () => [
        'chat',
        'market',
        'buy',
        'payment.finalize',
        'voice-room',
        'video-room',
        'video-room-setup',
        'voice-room-setup',
        'rewards',
        'rewards-mobile',
        'tasks',
        'tasks-mobile',
        'tawk-chat',
      ],
    },
  },

  data() {
    return {
      isExpanded: false,
      isMobile: false,
    }
  },

  computed: {
    userFreeCallUser() {
      return this.$store.getters['auth/freeCallUser']
    },
    shouldShow() {
      // Moments tam ekran video akışında sağ kenardaki eylem sütununun ve alttaki butonların üstüne biniyordu
      const isMomentsFeed = this.$route.name === 'moments' && this.$route.query.tab === 'discover'
      return !this.userFreeCallUser && !this.excludedRoutes.includes(this.$route.name) && !isMomentsFeed
    },
  },

  mounted() {
    this.checkViewport()
    window.addEventListener('resize', this.checkViewport)
    document.addEventListener('click', this.handleClickOutside)
    
    // Sayfa açıldığında dikkat çekmek için kısa süre expanded göster
    this.isExpanded = true
    setTimeout(() => {
      this.isExpanded = false
    }, 2000) // 2 saniye sonra peek state'e dön
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkViewport)
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    checkViewport() {
      this.isMobile = window.innerWidth <= 768
    },

    handleClick() {
      if (this.isExpanded) {
        this.$router.push(this.targetRoute)
        this.$emit('navigate', this.targetRoute)
      } else {
        this.isExpanded = true
        this.$emit('expand')
      }
    },

    handleMouseEnter() {
      if (!this.isMobile) {
        this.isExpanded = true
        this.$emit('expand')
      }
    },

    handleMouseLeave() {
      if (!this.isMobile) {
        this.isExpanded = false
        this.$emit('collapse')
      }
    },

    handleClickOutside(event) {
      if (this.isMobile && this.isExpanded) {
        if (!this.$el.contains(event.target)) {
          this.isExpanded = false
          this.$emit('collapse')
        }
      }
    },
  },
}
</script>


<style lang="scss" scoped>
.floating-promo-button {
  --floating-btn-width: 240px;
  --floating-btn-height: 56px;
  --floating-btn-peek-offset: 75%;
  --floating-btn-gradient-start: #1a237e;
  --floating-btn-gradient-end: #3949ab;
  --floating-btn-border-radius: 28px 0 0 28px;
  --floating-btn-transition-duration: 0.3s;
  --floating-btn-z-index: 999;

  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%) translateX(var(--floating-btn-peek-offset));
  width: var(--floating-btn-width);
  height: var(--floating-btn-height);
  z-index: var(--floating-btn-z-index);
  cursor: pointer;
  transition: transform var(--floating-btn-transition-duration) ease;

  &.is-expanded {
    transform: translateY(-50%) translateX(0);
  }

  &:focus {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  &__content {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    padding-right: 16px;
    background: linear-gradient(135deg, var(--floating-btn-gradient-start), var(--floating-btn-gradient-end));
    border-radius: var(--floating-btn-border-radius);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    position: relative;
  }

  &__icon-img {
    flex-shrink: 0;
    height: 100%;
    width: auto;
    object-fit: cover;
    border-radius: 28px 0 0 28px;
  }

  &__text {
    color: #FCD377;
    font-family: 'Lexend', sans-serif;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  &__stars {
    position: absolute;
    top: -15px;
    left: -15px;
    right: -15px;
    bottom: -15px;
    pointer-events: none;
    overflow: visible;
  }

  .star {
    position: absolute;
    color: #FCD377;
    opacity: 0;
    animation: starTwinkle 2s ease-in-out infinite;
    text-shadow: 0 0 4px rgba(252, 211, 119, 0.8);

    &--1 {
      top: -5px;
      left: 25%;
      font-size: 12px;
      animation-delay: 0s;
    }

    &--2 {
      top: 0;
      right: 20%;
      font-size: 10px;
      animation-delay: 0.4s;
    }

    &--3 {
      bottom: -5px;
      left: 35%;
      font-size: 8px;
      animation-delay: 0.8s;
    }

    &--4 {
      bottom: 0;
      right: 25%;
      font-size: 11px;
      animation-delay: 1.2s;
    }

    &--5 {
      top: 50%;
      left: -5px;
      font-size: 9px;
      animation-delay: 0.6s;
      transform: translateY(-50%);
    }

    &--6 {
      top: 30%;
      left: 15%;
      font-size: 7px;
      animation-delay: 1.5s;
    }
  }

  &__sparkle {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    overflow: hidden;
    border-radius: var(--floating-btn-border-radius);

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
    }

    &::before {
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.2) 50%,
        transparent 100%
      );
      animation: shimmer 2s infinite;
    }

    &::after {
      background: radial-gradient(
        circle at 20% 50%,
        rgba(255, 255, 255, 0.3) 0%,
        transparent 50%
      );
      animation: pulse 1.5s ease-in-out infinite;
    }
  }
}

// Slide-in animation for Vue transition
.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.slide-in-enter,
.slide-in-leave-to {
  transform: translateY(-50%) translateX(100%);
  opacity: 0;
}

// Shimmer animation
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// Pulse animation
@keyframes pulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
}

// Star twinkle animation
@keyframes starTwinkle {
  0%, 100% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

// Mobile adjustments
@media (max-width: 768px) {
  .floating-promo-button {
    --floating-btn-width: 220px;
    --floating-btn-height: 48px;
    top: 35%;

    &__text {
      font-size: 12px;
    }
  }
}
</style>
